<template>
    <div class="relative">
        <div class="flex space-x-2">
            <input
                :id="id"
                ref="input"
                v-model="modelValue"
                class="flex-auto placeholder-gray-400 border border-gray-300 rounded-md hover:placeholder-gray-700 focus:border-brand-300 focus:ring focus:ring-brand-200/50 dark:bg-zinc-900 dark:border-zinc-700"
                :class="[sizeClass, { 'bg-gray-50 cursor-not-allowed': props.disabled }]"
                :placeholder="props.placeholder"
                :type="props.type"
                :dusk="dusk"
                :autocomplete="autocomplete"
                :required="required"
                :disabled="disabled"
                :min="props.min"
                :max="props.max"
                :tabindex="props.tabindex"
                data-1p-ignore
            />

            <div v-if="brand !== 'unknown'" class="flex items-center grow-0 w-10">
                <icon :icon="`cards/${brand}`" class="h-6 max-w-full"></icon>
            </div>
        </div>

        <div v-if="props.valid" class="absolute text-green-800 -translate-y-1/2 top-1/2 right-4">
            <icon icon="hero/outline/check" class="w-4 h-4"></icon>
        </div>

        <div v-if="props.invalid" class="absolute text-red-800 -translate-y-1/2 top-1/2 right-4">
            <icon icon="hero/outline/exclamation-circle" class="w-4 h-4"></icon>
        </div>
    </div>
</template>

<script setup lang="ts">
import Cleave from 'cleave.js';
import { computed, onMounted, ref } from 'vue';
import type { Ref } from 'vue';
import { ComponentSize } from '@src/Common/DataGrid/types';

const modelValue = defineModel({
    type: String,
});

interface Props {
    placeholder?: string | number | undefined;
    value?: string | number | null;
    size?: ComponentSize;
    dusk?: string | null;
    type?: string;
    valid?: boolean;
    invalid?: boolean;
    autocomplete?: string;
    id?: string | null;
    required?: boolean;
    disabled?: boolean;
    min?: string | number | null;
    max?: string | number | null;
    tabindex?: number | string | null;
}

const props = withDefaults(defineProps<Props>(), {
    placeholder: undefined,
    value: null,
    size: 'base',
    type: 'text',
    dusk: null,
    valid: false,
    invalid: false,
    autocomplete: 'off',
    id: null,
    required: false,
    disabled: false,
    min: null,
    max: null,
    tabindex: null,
});

const sizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'px-1 py-0.5 text-xs w-full',
        sm: 'px-2 py-1 text-sm w-full',
        base: 'px-3 py-1.5 text-base w-full',
        lg: 'px-4 py-2 text-lg w-full',
        xl: 'px-5 py-2.5 text-xl w-full',
    };

    return `${sizes[props.size] ?? sizes.base} ${props.valid || props.invalid ? 'pr-8' : ''}`;
});

// Store reference for input
const input = ref(null);
const brand = ref('unknown');

onMounted(() => {
    // Bind Cleave.js to input
    input.value.cleave = new Cleave(input.value, {
        creditCard: true,
        onCreditCardTypeChanged: (type) => {
            brand.value = type;
        },
    });

    input.value.oninput = (event) => {
        modelValue.value = event.target.value;
    };
});
</script>
